import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuditHistory } from '../hooks/useAudit';
import Button from '../components/Button';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';
import { trackEvent } from '../config/analytics';

// Safe date formatter that handles Firestore timestamps, ISO strings, and invalid values
const safeFormatDate = (dateValue, formatStr = 'MMM d, yyyy') => {
  try {
    if (!dateValue) return '-';

    let date;
    if (dateValue?.seconds) {
      // Firestore Timestamp
      date = new Date(dateValue.seconds * 1000);
    } else if (dateValue?.toDate) {
      // Firestore Timestamp with toDate method
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      return '-';
    }

    // Check if date is valid
    if (isNaN(date.getTime())) return '-';

    return format(date, formatStr);
  } catch (err) {
    console.error('Date formatting error:', err);
    return '-';
  }
};

const DashboardPage = () => {
  const { userData, refreshUserData, isAdmin } = useAuth();
  const { audits, loading: auditsLoading } = useAuditHistory({ limit: 5 });
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent('dashboard_viewed');
    refreshUserData();
  }, [refreshUserData]);

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };
    return badges[status] || badges.pending;
  };

  const getSubscriptionBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      trialing: 'bg-blue-100 text-blue-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      none: 'bg-gray-100 text-gray-800',
    };
    return badges[status] || badges.none;
  };

  const getTierName = (tier) => {
    const names = {
      small: 'Small Business',
      standard: 'Standard',
      full: 'Full Enterprise',
    };
    return names[tier] || 'No Plan';
  };

  if (auditsLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner size="lg" text="Loading dashboard..." />
        </div>
      </div>
    );
  }

  // Admins always have access, regular users need active subscription
  const hasActiveSubscription = isAdmin || userData?.subscriptionStatus === 'active';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {userData?.displayName || 'User'}!
            {isAdmin && <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">Admin</span>}
          </h1>
          <p className="mt-2 text-gray-600">
            Here's an overview of your accessibility audits and account status.
          </p>
        </div>

        {/* No Subscription Warning - don't show for admins */}
        {!hasActiveSubscription && (
          <div className="mb-8 bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
            <div className="flex items-start">
              <span className="text-yellow-400 text-2xl mr-4">⚠</span>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                  No Active Subscription
                </h3>
                <p className="text-yellow-700 mb-4">
                  You need an active subscription to run audits. Choose a plan to get started.
                </p>
                <Button
                  onClick={() => {
                    trackEvent('upgrade_clicked', { location: 'dashboard_warning' });
                    navigate('/subscription');
                  }}
                >
                  View Plans
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Subscription Status Card */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Subscription</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSubscriptionBadge(userData?.subscriptionStatus)}`}>
                {userData?.subscriptionStatus || 'none'}
              </span>
            </div>
            <p className="text-2xl font-bold text-indigo-600 mb-2">
              {getTierName(userData?.subscriptionTier)}
            </p>
            <Link to="/subscription" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
              Manage subscription →
            </Link>
          </Card>

          {/* Audits Usage Card */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Audits This Month</h3>
            {isAdmin ? (
              <>
                <p className="text-2xl font-bold text-green-600 mb-2">Unlimited</p>
                <p className="text-xs text-gray-500">Admin access - no limits</p>
              </>
            ) : (
              <>
                <div className="flex items-end space-x-2 mb-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {userData?.auditsUsed || 0}
                  </span>
                  <span className="text-xl text-gray-500 mb-1">
                    / {userData?.auditsLimit || 0}
                  </span>
                </div>
                {userData?.auditsLimit && userData?.auditsUsed && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${Math.min((userData.auditsUsed / userData.auditsLimit) * 100, 100)}%` }}
                    ></div>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  {userData?.nextResetDate
                    ? `Resets ${safeFormatDate(userData.nextResetDate)}`
                    : 'Subscribe to start auditing'}
                </p>
              </>
            )}
          </Card>

          {/* Total Audits Card */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Audits</h3>
            <p className="text-3xl font-bold text-gray-900 mb-2">
              {audits?.length || 0}
            </p>
            <Link to="/history" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
              View all audits →
            </Link>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card hover className="cursor-pointer" onClick={() => navigate('/audit/new')}>
              <div className="flex items-center space-x-4">
                <div className="bg-indigo-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">New Audit</h3>
                  <p className="text-sm text-gray-600">Start a new accessibility audit</p>
                </div>
              </div>
            </Card>

            <Card hover className="cursor-pointer" onClick={() => navigate('/history')}>
              <div className="flex items-center space-x-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">View History</h3>
                  <p className="text-sm text-gray-600">See past audit reports</p>
                </div>
              </div>
            </Card>

            <Card hover className="cursor-pointer" onClick={() => navigate('/settings')}>
              <div className="flex items-center space-x-4">
                <div className="bg-gray-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Settings</h3>
                  <p className="text-sm text-gray-600">Update your preferences</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Recent Audits */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Recent Audits</h2>
            {audits && audits.length > 0 && (
              <Link to="/history" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                View all →
              </Link>
            )}
          </div>

          {!audits || audits.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900">No audits yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by running your first accessibility audit.
                </p>
                <div className="mt-6">
                  <Button onClick={() => navigate('/audit/new')}>
                    Start Your First Audit
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Issues
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {audits.map((audit) => (
                    <tr key={audit.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {audit.url}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(audit.status)}`}>
                          {audit.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {audit.status === 'completed' && audit.summary
                          ? `${audit.summary.critical || 0} critical, ${audit.summary.serious || 0} serious`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {safeFormatDate(audit.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/audit/${audit.id}`}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
